<template>
  <div>
    <div v-if="isMobile && !collapsed" class="overlay" @click="toggleCollapse"></div>
    <div class="sidebar" :class="{ collapsed, mobile: isMobile }">
      <div class="logo">
        <a href="/"><img src="@/assets/images/logo-b.svg" alt="Logo" class="logo-img" /></a>
      </div>

      <a-menu
          id="sidebar-menu"
          v-model:openKeys="openKeys"
          v-model:selectedKeys="selectedKeys"
          theme="light"
          mode="inline"
          :inline-collapsed="collapsed"
          :items="items.value"
          class="sidebar-menu"
          @click="handleMenuClick"
      >
        <template #expandIcon="{ isOpen }">
          <DownOutlined v-if="!isOpen" :style="{fontSize: '12px'}" />
          <UpOutlined v-else :style="{fontSize: '12px'}" />
        </template>
      </a-menu>

      <div class="last-divider" />

      <div class="bottom-wrap">
        <a v-if="!collapsed" href="https://way2wei.ru" class="footer-text" target="_blank">На сайт way2wei.ru</a>
        <a v-else href="https://way2wei.ru" class="footer-icon-wrapper" target="_blank">
          <img
              src="@/assets/images/icons/second-menu/RedirectSiteIcon.svg"
              class="footer-icon"
              alt="Site Icon"
          />
        </a>
        <div class="collapse-toggle" @click="toggleCollapse">
          <img
              src="@/assets/images/icons/second-menu/CollapseIcon.svg"
              class="collapse-icon"
              alt="Toggle"
          />
        </div>
      </div>
    </div>

    <button v-if="isMobile" class="burger" @click="toggleCollapse">
      <MenuOutlined v-if="collapsed" />
      <CloseOutlined v-else />
    </button>
  </div>
</template>

<script>
import {ref, reactive, h, onMounted, onBeforeUnmount, watch, computed} from 'vue'
import router from '@/router'

import VectorHomeIcon from '@/assets/images/icons/second-menu/Vector-home.svg'
import VectorAnalyticsIcon from '@/assets/images/icons/second-menu/AnalyticsIcon.svg'
import VectorOrdersIcon from '@/assets/images/icons/second-menu/OrdersIcon.svg'
import VectorProfileIcon from '@/assets/images/icons/second-menu/ProfileIcon.svg'
import VectorToolsIcon from '@/assets/images/icons/second-menu/ToolsIcon.svg'
import VectorSupportIcon from '@/assets/images/icons/second-menu/SupportIcon.svg'
import VectorMyDevelopmentIcon from '@/assets/images/icons/second-menu/DevelopmentIcon.svg'
import VectorGradeIcon from '@/assets/images/icons/second-menu/GradeIcon.svg'
import VectorStaffIcon from '@/assets/images/icons/second-menu/StaffIcon.svg'
import VectorEmotionDiaryIcon from '@/assets/images/icons/second-menu/EmotionDiaryIcon.svg';
import VectorTrackerIcon from '@/assets/images/icons/second-menu/ProhorIcon.svg';
import VectorHelpIcon from '@/assets/images/icons/second-menu/HelpIcon.svg';
import VectorCompanyCoursesIcon from '@/assets/images/icons/second-menu/CompanyCoursesIcon.svg';

import { MenuOutlined, CloseOutlined, DownOutlined, UpOutlined } from '@ant-design/icons-vue'
import {useRoute} from "vue-router";
import useCheckRights from "@/mixins/useCheckRights";
import {useStore} from "vuex";

export default {
  name: 'main-sidebar',
  components: { MenuOutlined, CloseOutlined, DownOutlined, UpOutlined },
  props: {
    backUrl: String,
  },
  setup(props, context) {
    const route = useRoute();
    const store = useStore();
    const {getRight} = useCheckRights();

    const collapsed = ref(false);
    const isMobile = ref(false);
    const selectedKeys = ref([]);
    const openKeys = ref([]);

    const profile = computed(() => store.state.profile.profile);

    const handleMenuClick = (e) => {
      if (e.key) router.push(e.key)
      if (isMobile.value) collapsed.value = true
    }

    const { emit } = context

    const toggleCollapse = () => {
      collapsed.value = !collapsed.value
    }

    const getSvgIcon = (iconSrc) =>
        () =>
            h('span', {
              class: 'sidebar-icon',
              style: { '--icon': `url(${iconSrc})` }
            });

    const items = reactive({value: []});
    const preparedItems = reactive({ value: [
      { key: '/', icon: getSvgIcon(VectorHomeIcon), label: 'Моя компания', additionalType: 'company' },
      { key: 'users',
        icon: getSvgIcon(VectorStaffIcon),
        label: 'Пользователи',
        additionalType: 'users',
        children: [
          { key: '/staff', label: 'Сотрудники' },
          { key: '/groups', label: 'Группы' },
        ],
      },
      {
        key: '/rating',
        icon: getSvgIcon(VectorGradeIcon),
        label: 'Оценка',
        additionalType: 'rating',
      },
      {
        key: '/instruments',
        icon: getSvgIcon(VectorToolsIcon),
        label: 'Ежедневные помощники',
        additionalType: 'tools',
      },
      {
        key: '/courses',
        icon: getSvgIcon(VectorCompanyCoursesIcon),
        label: 'Развитие',
        additionalType: 'development',
      },
      {
        key: '/analytics',
        icon: getSvgIcon(VectorAnalyticsIcon),
        label: 'Аналитика',
        additionalType: 'analytics',
      },
      { key: '/my-orders', icon: getSvgIcon(VectorOrdersIcon), label: 'Заказы и подписки', additionalType: 'orders' },
      { type: 'divider', additionalType: 'company-divider' },
      { key: '/personal', icon: getSvgIcon(VectorProfileIcon), label: 'Личный кабинет', additionalType: 'personal' },
      {
        key: '/my-development',
        icon: getSvgIcon(VectorMyDevelopmentIcon),
        label: 'Мое развитие',
        additionalType: 'my-development',
      },
      {
        key: '/diary',
        icon: getSvgIcon(VectorEmotionDiaryIcon),
        label: 'Дневник Эмоций',
        additionalType: 'my-development',
      },
      {
        key: '/mood/tracker',
        icon: getSvgIcon(VectorTrackerIcon),
        label: 'Навигатор эмоций',
        additionalType: 'my-development',
      },
      { type: 'divider' },
      { key: '/faq', icon: getSvgIcon(VectorHelpIcon), label: 'Помощь с ЛК', additionalType: 'help' },
      { key: '/support', icon: getSvgIcon(VectorSupportIcon), label: 'Поддержка', additionalType: 'support' },
    ]
  })

    watch(collapsed, (newVal) => {
      emit('toggle', newVal)
    })

    const handleResize = () => {
      const width = window.innerWidth
      if (width <= 768) {
        isMobile.value = true
        collapsed.value = true
      } else if (width <= 1024) {
        isMobile.value = false
        collapsed.value = true
      } else {
        isMobile.value = false
        collapsed.value = false
      }
    }

    const adminMenuKeys = ['orders', 'development', 'tools', 'analytics', 'rating', 'company', 'company-divider', 'users'];

    onMounted(() => {
      handleResize()
      window.addEventListener('resize', handleResize);
    })

    onBeforeUnmount(() => {
      window.removeEventListener('resize', handleResize)
    });

    const specialRoute = computed(() => store.state.test.specialDetailRoute);
    const getEffectiveMenuKey = () => {
      if (route.name === 'detailed' && specialRoute.value) {
        return specialRoute.value;
      }

      return getMenuKeyFromRoute();
    };

    const findParentKeyByChildKey = (childKey) => {
      for (const item of items.value) {
        if (item?.children?.some((c) => c.key === childKey)) {
          return item.key;
        }
      }
      return null;
    };

    const getMenuKeyFromRoute = () => {
      const matched = route.matched;
      for (let i = matched.length - 1; i >= 0; i--) {
        const r = matched[i];
        if (r?.meta?.menuKey) return r.meta.menuKey;
      }

      return matched.at(-1)?.path || route.path;
    };

    watch(
        () => [getEffectiveMenuKey(), items.value],
        ([menuKey]) => {
          if (!menuKey) return;

          selectedKeys.value = [menuKey];
          const parentKey = findParentKeyByChildKey(menuKey);

          if (!collapsed.value) {
            openKeys.value = parentKey ? [parentKey] : [];
          }
        },
        { immediate: true, deep: true }
    );

    watch(
        () => profile.value,
        () => {
            if (profile.value && profile.value.role && (getRight('staff') || getRight('private_person'))) {
              items.value = preparedItems.value.filter((item) => {
                return !adminMenuKeys.includes(item.additionalType);
              });
            } else if (getRight('admin') || getRight('superadmin')) {
              items.value = preparedItems.value;
            }
        },
        { immediate: true }
    );

    return { collapsed, selectedKeys, openKeys, items, handleMenuClick, toggleCollapse, isMobile }
  }
}
</script>

<style scoped lang="scss">
.sidebar {
  background: #fff;
  height: 100vh;
  position: fixed;
  left: 0;
  top: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  z-index: 1000;
  width: 260px;
  min-width: 220px;
  transition: width 0.3s, transform 0.3s ease-in-out;
}

.sidebar-menu :deep(.ant-menu-item-selected) {
  background-color: var(--focus-color) !important;
  color: var(--main-green) !important;
  z-index: 999;
}

.collapsed .sidebar-menu :deep(.ant-menu-item),
.collapsed .sidebar-menu :deep(.ant-menu-submenu-selected),
.collapsed .sidebar-menu :deep(.ant-menu-submenu-title) {
  display: flex;
  align-items: center;
}

.sidebar-menu :deep(:where(.ant-menu-submenu-selected) > .ant-menu-submenu-title) {
  color: var(--main-font) !important;
}

.sidebar-menu :deep(.ant-menu-item-divider) {
  margin-top: 8px;
  margin-bottom: 8px;
}

.sidebar.collapsed {
  width: 80px;
  min-width: 80px;
}

.sidebar.mobile {
  transform: translateX(-100%);
}
.sidebar.mobile:not(.collapsed) {
  transform: translateX(0);
}

.logo {
  margin: 20px 0 10px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  flex-shrink: 0;
}

.logo-img {
  height: 36px;
  transition: all 0.2s;
}

.sidebar.collapsed .logo {
  height: 50px;
  padding: 0;
  margin: 0;
}

.sidebar.collapsed .logo-img {
  height: 24px;
  width: 55px;
}

.sidebar-menu {
  flex: 1;
  padding: 10px;
  border-right: none;
}

.last-divider {
  height: 1px;
  background: rgba(0, 0, 0, 0.06);
  margin: 8px 12px;
}

.bottom-wrap {
  padding: 8px 16px 8px 26px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
}

.collapsed .bottom-wrap {
  padding-left: 17.5px;
}

.footer-text {
  font-size: 12px;
  color: var(--grey-text);
}

.footer-text:hover {
  color: var(--main-green);
}

.footer-icon {
  display: none;
}

.footer-icon-wrapper {
  margin: auto;
}

.sidebar.collapsed .footer-text {
  display: none;
}
.sidebar.collapsed .footer-icon {
  display: block;
  width: 16px;
  height: 16px;
  margin: 0 auto;
}

.collapse-toggle {
  cursor: pointer;
}

.collapsed .collapse-toggle {
  margin: 8px auto auto;
  padding-right: 2px;
  text-align: left;
}

.collapse-icon {
  width: 16px;
  height: 16px;
  transition: transform 0.2s;
}
.sidebar.collapsed .collapse-icon {
  transform: rotate(180deg);
}

@media (max-width: 1024px) {
  .sidebar {
    width: 200px;
  }
}

@media (max-width: $mobile-breakpoint) {
  .logo {
    display: none;
  }

  .sidebar {
    margin-top: 62px;
    position: fixed;
    width: 240px;
    height: 100vh;
    transform: translateX(-100%);
  }

  .bottom-wrap {
    margin-bottom: 62px;
  }

  .burger {
    position: fixed;
    top: 20px;
    left: 14px;
    width: 28px;
    height: 24px;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    background: transparent;
    border: none;
    z-index: 5;
    cursor: pointer;
  }

  .overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    z-index: 900;
  }
}

:deep(.ant-menu-item),
:deep(.ant-menu-submenu-title),
:deep(.ant-menu-title-content) {
  white-space: normal !important;
  line-height: 120% !important;
  overflow: visible !important;
  text-overflow: unset !important;
}

:deep(.sidebar-icon) {
  width: 16px;
  height: 16px;
  display: inline-block;
  vertical-align: middle;
  mask: var(--icon) center/contain no-repeat;
  -webkit-mask: var(--icon) center/contain no-repeat;
  background-color: var(--main-font);
}

:deep(.ant-menu-item-selected .sidebar-icon ) {
  background-color: var(--main-green);
}

</style>
